import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { AuthenticationModel } from 'src/app/authentication/authentication.model';
import { SegmentService } from 'shared/services';
import { AutoUnsubscribe, getSupportEmail } from 'helpers';
import { SignupErrorType } from '../../types';
import { AuthService } from '@auth0/auth0-angular';

@Component({
    selector: 'fc-signup-finish',
    templateUrl: './signup-finish.component.html',
    standalone: false
})
@AutoUnsubscribe
export class SignupFinishComponent implements OnInit {
    supportEmail = getSupportEmail();
    error$: Subject<string> = new BehaviorSubject(null);

    private _subscription = new Subscription();

    constructor(
        private authenticationModel: AuthenticationModel,
        private segmentService: SegmentService,
        private authService: AuthService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this._subscription.add(
            this.route.params
                .pipe(
                    filter((params) => params.requestId),
                    switchMap((params) => {
                        return this.authenticationModel.getSignupRequest(params.requestId).pipe(
                            catchError((error) => {
                                const errorType: string = _.get(error, 'data.type', SignupErrorType.UnknownError);
                                this.error$.next(errorType);

                                return of(null);
                            })
                        );
                    })
                )
                .subscribe(async (signupRequest) => {
                    if (signupRequest) {
                        await this.finishSignup(signupRequest);
                        this.goToLogin();
                    }
                })
        );
    }

    retry() {
        this.error$.next(null);
        window.location.reload();
    }

    private async finishSignup(signupRequest) {
        try {
            await this.authenticationModel.signup(signupRequest._id);
            this.segmentService.track('New user signup with company data', {
                ..._.pick(signupRequest.user, 'firstname', 'surname'),
                username: signupRequest.user.email,
                organizationType: signupRequest.organization.type,
                company: signupRequest.organization.name
            });
            return;
        } catch (error) {
            const errorType: string = _.get(error, 'data.type', SignupErrorType.UnknownError);

            this.error$.next(errorType);

            if (errorType === SignupErrorType.UserAlreadyExistsError) {
                return;
            }

            this.segmentService.track('New user signup with company data failed', {
                ..._.pick(signupRequest.user, 'firstname', 'surname'),
                username: signupRequest.user.email,
                organizationType: signupRequest.organization.type,
                company: signupRequest.organization.name
            });
        }
    }

    private goToLogin() {
        this._subscription.add(this.authService.loginWithRedirect().subscribe());
    }
}
